import { useAuth } from "../context/AuthContext";
import { useCallback /* Remove useState, useEffect, useContext if not used elsewhere */ } from "react";
import axios from "axios";
import { useState } from "react";
import api from './api'; // Import the configured axios instance
import { useMutation, useQueryClient } from 'react-query';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api'; // Define API_BASE_URL

// Helper function for making fetch requests
const fetchApi = async (url, options = {}) => {
    const response = await fetch(url, options);
    if (!response.ok) {
        let errorData;
        try {
            // Try to parse error as JSON first
            errorData = await response.json();
        } catch (e) {
            // If JSON parsing fails, try to get text
            try {
                const errorText = await response.text();
                errorData = { message: errorText || response.statusText }; 
            } catch (textErr) {
                // If text also fails, use status text
                errorData = { message: response.statusText };
            }
        }
        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        error.details = errorData.details;
        error.status = response.status;
        throw error;
    }
    if (response.status === 204) {
        return null; // No content
    }
    
    // Check expected response type from options
    if (options.responseType === 'text') {
        return response.text(); // Return plain text
    }
    if (options.responseType === 'blob') {
        return response.blob(); // Return Blob object
    }

    // Default: Check content-type header
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
         return response.json(); // Parse as JSON
    } else {
         // Fallback for unexpected content types (e.g., text/html, text/plain)
         // Treat as text as a reasonable default
         console.warn('fetchApi received non-JSON response when not expecting text/blob for:', url, '- Returning as text.');
         return response.text(); 
    }
};

// --- Individual Hook Exports --- //

// --- Authentication --- //
// loginUser is kept separate as it doesn't return a hook
export const loginUser = (username, password) => {
    return fetchApi(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
    });
};
// useLogin remains separate as it has its own state
// export const useLogin = () => { ... }; // Keep the existing useLogin hook

export const useGetUserProfile = () => {
    const { token } = useAuth();
    return useCallback(() => {
        if (!token) return Promise.reject(new Error('Not authenticated'));
        return fetchApi(`${API_BASE_URL}/auth/profile`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
    }, [token]);
};

// --- App Management --- //
const _listApps = (token) => fetchApi(`${API_BASE_URL}/apps`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListApps = () => {
    const { token } = useAuth();
    return useCallback(() => _listApps(token), [token]);
};

const _getAppDetails = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useGetAppDetails = () => {
    const { token } = useAuth();
    return useCallback((appId) => _getAppDetails(token, appId), [token]);
};

const _createApp = (token, appData) => fetchApi(`${API_BASE_URL}/apps`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(appData) });
export const useCreateApp = () => {
    const { token } = useAuth();
    return useCallback((appData) => _createApp(token, appData), [token]);
};

const _updateApp = (token, appId, appData) => fetchApi(`${API_BASE_URL}/apps/${appId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(appData) });
export const useUpdateApp = () => {
    const { token } = useAuth();
    return useCallback((appId, appData) => _updateApp(token, appId, appData), [token]);
};

const _deleteApp = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
export const useDeleteApp = () => {
    const { token } = useAuth();
    return useCallback((appId) => _deleteApp(token, appId), [token]);
};

// --- Articles --- //
const _listArticles = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/articles`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListArticles = () => {
    const { token } = useAuth();
    return useCallback((appId) => _listArticles(token, appId), [token]);
};

const _getArticle = (token, appId, articleId) => fetchApi(`${API_BASE_URL}/apps/${appId}/articles/${articleId}`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useGetArticle = () => {
    const { token } = useAuth();
    return useCallback((appId, articleId) => _getArticle(token, appId, articleId), [token]);
};

const _createArticle = (token, appId, articleData) => fetchApi(`${API_BASE_URL}/apps/${appId}/articles`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(articleData) });
export const useCreateArticle = () => {
    const { token } = useAuth();
    return useCallback((appId, articleData) => _createArticle(token, appId, articleData), [token]);
};

const _updateArticle = (token, appId, articleId, articleData) => fetchApi(`${API_BASE_URL}/apps/${appId}/articles/${articleId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(articleData) });
export const useUpdateArticle = () => {
    const { token } = useAuth();
    return useCallback((appId, articleId, articleData) => _updateArticle(token, appId, articleId, articleData), [token]);
};

const _deleteArticle = (token, appId, articleId) => fetchApi(`${API_BASE_URL}/apps/${appId}/articles/${articleId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
export const useDeleteArticle = () => {
    const { token } = useAuth();
    return useCallback((appId, articleId) => _deleteArticle(token, appId, articleId), [token]);
};

// --- Article Comments --- //
const _listArticleComments = (token, appId, articleId) => fetchApi(`${API_BASE_URL}/apps/${appId}/articles/${articleId}/comments`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListArticleComments = () => {
    const { token } = useAuth();
    return useCallback((appId, articleId) => _listArticleComments(token, appId, articleId), [token]);
};

const _deleteArticleComment = (token, appId, articleId, commentId) => fetchApi(`${API_BASE_URL}/apps/${appId}/articles/${articleId}/comments/${commentId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
export const useDeleteArticleComment = () => {
    const { token } = useAuth();
    return useCallback((appId, articleId, commentId) => _deleteArticleComment(token, appId, articleId, commentId), [token]);
};

// --- Article Filters --- //
export const useListArticleFilters = () => {
    const { token } = useAuth();
    return useCallback(async (appId) => {
        if (!appId) throw new Error("App ID is required");
        const options = {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/article-filters`, options);
    }, [token]);
};

export const useCreateArticleFilter = () => {
    const { token } = useAuth();
    return useCallback(async (appId, filterData) => {
        if (!appId) throw new Error("App ID is required");
        const options = {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(filterData)
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/article-filters`, options);
    }, [token]);
};

export const useUpdateArticleFilter = () => {
    const { token } = useAuth();
    return useCallback(async (appId, filterId, filterData) => {
        if (!appId || !filterId) throw new Error("App ID and Filter ID are required");
        const options = {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
             },
            body: JSON.stringify(filterData)
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/article-filters/${filterId}`, options);
    }, [token]);
};

export const useDeleteArticleFilter = () => {
    const { token } = useAuth();
    return useCallback(async (appId, filterId) => {
        if (!appId || !filterId) throw new Error("App ID and Filter ID are required");
         const options = {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/article-filters/${filterId}`, options);
    }, [token]);
};

// --- Storage --- //
const _uploadFile = async (token, appId, file, storagePath, options = {}) => {
    const formData = new FormData();
    const fieldName = options.fieldName || 'image';
    const endpoint = options.endpoint || 'upload';
    formData.append(fieldName, file);

    const url = `${API_BASE_URL}/apps/${appId}/storage/${endpoint}?path=${encodeURIComponent(storagePath || '')}`;
    
    try {
        // Use fetch directly to ensure correct Content-Type for FormData
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                // DO NOT set 'Content-Type': 'multipart/form-data'; 
                // The browser sets it automatically with the correct boundary for FormData.
            },
            body: formData,
        });

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch (e) {
                errorData = { message: response.statusText };
            }
            const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
            error.details = errorData.details;
            error.status = response.status;
            throw error;
        }
        
        // Handle 204 No Content or parse JSON response
        if (response.status === 204) {
            return null; 
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
             return response.json();
        } else {
             // Should return JSON with { url: '...' } on success (200 OK)
             console.warn('Upload response was not JSON as expected.');
             return null; 
        }

    } catch (error) {
        console.error('Error during file upload:', error);
        throw error; // Re-throw to be caught by the calling hook
    }
};

// Keep specific hooks for semantic clarity, adjust path param as needed
export const useUploadImage = () => { // For Articles
    const { token } = useAuth();
    return useCallback((appId, file) => _uploadFile(token, appId, file, 'article_images'), [token]);
};
export const useUploadDocument = () => { // For article attachments
    const { token } = useAuth();
    return useCallback(
        (appId, file) => _uploadFile(token, appId, file, 'attachments', { fieldName: 'file', endpoint: 'upload-document' }),
        [token]
    );
};
export const useUploadChatImage = () => { // For Chat
    const { token } = useAuth();
    return useCallback((appId, file) => _uploadFile(token, appId, file, 'chat_images'), [token]);
};
// ADDED: Specific hook for Event images
export const useUploadEventImage = () => { 
    const { token } = useAuth();
    return useCallback((appId, file) => _uploadFile(token, appId, file, 'event_images'), [token]);
};

// --- Organizations --- //
export const useListOrganizations = () => {
    const { token } = useAuth();
    const fetchOrganizations = useCallback(async (appId) => {
        if (!token || !appId) {
            console.error('Auth token or App ID missing for listing organizations');
            throw new Error('Authentication or App ID is required.');
        }
        try {
            // Corrected endpoint
            const response = await axios.get(`${API_BASE_URL}/apps/${appId}/organizations/get-all`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching organizations:', error.response?.data || error.message);
            throw new Error(error.response?.data?.message || 'Failed to fetch organizations');
        }
    }, [token]);

    return fetchOrganizations;
};



// --- Organization CRUD ---
const _createOrganization = (token, appId, orgData) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/organizations`,
    {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(orgData),
    }
);
export const useCreateOrganization = () => {
    const { token } = useAuth();
    return useCallback((appId, orgData) => _createOrganization(token, appId, orgData), [token]);
};

const _updateOrganization = (token, appId, orgId, orgData) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/organizations/${orgId}`,
    {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(orgData),
    }
);
export const useUpdateOrganization = () => {
    const { token } = useAuth();
    return useCallback((appId, orgId, orgData) => _updateOrganization(token, appId, orgId, orgData), [token]);
};

const _deleteOrganization = (token, appId, orgId) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/organizations/${orgId}`,
    { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
);
export const useDeleteOrganization = () => {
    const { token } = useAuth();
    return useCallback((appId, orgId) => _deleteOrganization(token, appId, orgId), [token]);
};

const _getOrganizationUsers = (token, appId, orgId) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/organizations/${orgId}/users`,
    { headers: { 'Authorization': `Bearer ${token}` } }
);
export const useGetOrganizationUsers = () => {
    const { token } = useAuth();
    return useCallback((appId, orgId) => _getOrganizationUsers(token, appId, orgId), [token]);
};

const _assignUserToOrg = (token, appId, orgId, userData) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/organizations/${orgId}/users`,
    {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(userData),
    }
);
export const useOrgAssignUser = () => {
    const { token } = useAuth();
    return useCallback((appId, orgId, userData) => _assignUserToOrg(token, appId, orgId, userData), [token]);
};

const _removeUserFromOrg = (token, appId, orgId, userId) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/organizations/${orgId}/users/${userId}`,
    { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
);
export const useOrgRemoveUser = () => {
    const { token } = useAuth();
    return useCallback((appId, orgId, userId) => _removeUserFromOrg(token, appId, orgId, userId), [token]);
};

const _searchOrganizationUsers = (token, appId, query) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/organizations/search-users?q=${encodeURIComponent(query)}`,
    { headers: { 'Authorization': `Bearer ${token}` } }
);
export const useSearchOrganizationUsers = () => {
    const { token } = useAuth();
    return useCallback((appId, query) => _searchOrganizationUsers(token, appId, query), [token]);
};

// --- My Organizations (org_admin) --- //
const _getMyOrganizations = (token, appId) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/organizations/my-orgs`,
    { headers: { 'Authorization': `Bearer ${token}` } }
);
export const useGetMyOrganizations = () => {
    const { token } = useAuth();
    return useCallback((appId) => _getMyOrganizations(token, appId), [token]);
};

// --- Chat Groups --- //
const _listChatGroups = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListChatGroups = () => {
    const { token } = useAuth();
    return useCallback((appId) => _listChatGroups(token, appId), [token]);
};

const _createChatGroup = (token, appId, groupData) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(groupData) });
export const useCreateChatGroup = () => {
    const { token } = useAuth();
    return useCallback((appId, groupData) => _createChatGroup(token, appId, groupData), [token]);
};

const _updateChatGroup = (token, appId, groupId, groupData) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups/${groupId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(groupData) });
export const useUpdateChatGroup = () => {
    const { token } = useAuth();
    return useCallback((appId, groupId, groupData) => _updateChatGroup(token, appId, groupId, groupData), [token]);
};

const _deleteChatGroup = (token, appId, groupId) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups/${groupId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
export const useDeleteChatGroup = () => {
    const { token } = useAuth();
    return useCallback((appId, groupId) => _deleteChatGroup(token, appId, groupId), [token]);
};

const _listOrgsForChat = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups/organizations-list`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListOrgsForChat = () => {
    const { token } = useAuth();
    return useCallback((appId) => _listOrgsForChat(token, appId), [token]);
};

// --- Chat Messages & Comments --- //
const _listChatMessages = (token, appId, groupId) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups/${groupId}/messages`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListChatMessages = () => {
    const { token } = useAuth();
    return useCallback((appId, groupId) => _listChatMessages(token, appId, groupId), [token]);
};

const _listMessageComments = (token, appId, groupId, messageId) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups/${groupId}/messages/${messageId}/comments`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListMessageComments = () => {
    const { token } = useAuth();
    return useCallback((appId, groupId, messageId) => _listMessageComments(token, appId, groupId, messageId), [token]);
};

const _deleteMessageComment = (token, appId, groupId, messageId, commentId) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups/${groupId}/messages/${messageId}/comments/${commentId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
export const useDeleteMessageComment = () => {
    const { token } = useAuth();
    return useCallback((appId, groupId, messageId, commentId) => _deleteMessageComment(token, appId, groupId, messageId, commentId), [token]);
};

const _sendChatMessage = (token, appId, groupId, messageData) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups/${groupId}/messages`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(messageData)
    });
};
export const useSendChatMessage = () => {
    const { token } = useAuth();
    return useCallback((appId, groupId, messageData) => _sendChatMessage(token, appId, groupId, messageData), [token]);
};

// ADDED: Hook to delete a specific chat message
const _deleteChatMessage = (token, appId, groupId, messageId) => fetchApi(`${API_BASE_URL}/apps/${appId}/chat-groups/${groupId}/messages/${messageId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
export const useDeleteChatMessage = () => {
    const { token } = useAuth();
    return useCallback((appId, groupId, messageId) => _deleteChatMessage(token, appId, groupId, messageId), [token]);
};

// --- Admin Users --- //
const _listAdmins = (token) => fetchApi(`${API_BASE_URL}/admins`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListAdmins = () => {
    const { token } = useAuth();
    return useCallback(() => _listAdmins(token), [token]);
};

const _getAdmin = (token, adminId) => fetchApi(`${API_BASE_URL}/admins/${adminId}`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useGetAdmin = () => {
    const { token } = useAuth();
    return useCallback((adminId) => _getAdmin(token, adminId), [token]);
};

const _createAdmin = (token, adminData) => fetchApi(`${API_BASE_URL}/admins`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(adminData) });
export const useCreateAdmin = () => {
    const { token } = useAuth();
    return useCallback((adminData) => _createAdmin(token, adminData), [token]);
};

const _updateAdmin = (token, adminId, adminData) => fetchApi(`${API_BASE_URL}/admins/${adminId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(adminData) });
export const useUpdateAdmin = () => {
    const { token } = useAuth();
    return useCallback((adminId, adminData) => _updateAdmin(token, adminId, adminData), [token]);
};

const _deleteAdmin = (token, adminId) => fetchApi(`${API_BASE_URL}/admins/${adminId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
export const useDeleteAdmin = () => {
    const { token } = useAuth();
    return useCallback((adminId) => _deleteAdmin(token, adminId), [token]);
};

// --- Stats Service --- //
const _getAppStats = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useGetAppStats = () => {
    const { token } = useAuth();
    return useCallback((appId) => _getAppStats(token, appId), [token]);
}

// --- Reports Service --- //
const _getPublicationRanking = (token, appId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.from) searchParams.set('from', params.from);
    if (params.to) searchParams.set('to', params.to);
    if (params.types) searchParams.set('types', params.types);

    const queryString = searchParams.toString();
    return fetchApi(`${API_BASE_URL}/apps/${appId}/reports/publication-ranking${queryString ? `?${queryString}` : ''}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
};
export const useGetPublicationRanking = () => {
    const { token } = useAuth();
    return useCallback((appId, params) => _getPublicationRanking(token, appId, params), [token]);
}

// --- Event Hooks --- //
const _listEvents = (token, appId) => {
    if (!appId) throw new Error("App ID is required to list events.");
    return fetchApi(`${API_BASE_URL}/apps/${appId}/events`, {
        headers: { 'Authorization': `Bearer ${token}` },
    });
};
export const useListEvents = () => {
  const { token } = useAuth();
  return useCallback((appId) => _listEvents(token, appId), [token]);
};

const _getEventDetails = (token, appId, eventId) => {
    if (!appId || !eventId) throw new Error("App ID and Event ID are required.");
    return fetchApi(`${API_BASE_URL}/apps/${appId}/events/${eventId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
    });
};
export const useGetEventDetails = () => {
    const { token } = useAuth();
    return useCallback((appId, eventId) => _getEventDetails(token, appId, eventId), [token]);
};

const _createEvent = (token, appId, eventData) => {
    if (!appId) throw new Error("App ID is required to create an event.");
    return fetchApi(`${API_BASE_URL}/apps/${appId}/events`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(eventData),
    });
};
export const useCreateEvent = () => {
    const { token } = useAuth();
    return useCallback((appId, eventData) => _createEvent(token, appId, eventData), [token]);
};

const _updateEvent = (token, appId, eventId, eventData) => {
    if (!appId || !eventId) throw new Error("App ID and Event ID are required for update.");
    return fetchApi(`${API_BASE_URL}/apps/${appId}/events/${eventId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(eventData),
    });
};
export const useUpdateEvent = () => {
    const { token } = useAuth();
    return useCallback((appId, eventId, eventData) => _updateEvent(token, appId, eventId, eventData), [token]);
};

const _deleteEvent = (token, appId, eventId) => {
    if (!appId || !eventId) throw new Error("App ID and Event ID are required for delete.");
    return fetchApi(`${API_BASE_URL}/apps/${appId}/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
    });
};
export const useDeleteEvent = () => {
    const { token } = useAuth();
    return useCallback((appId, eventId) => _deleteEvent(token, appId, eventId), [token]);
};

const _listEventComments = async (token, appId, eventId) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/events/${eventId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
};
export const useListEventComments = () => {
    const { token } = useAuth();
    return useCallback((appId, eventId) => _listEventComments(token, appId, eventId), [token]);
};

const _listEventAttendees = async (token, appId, eventId) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/events/${eventId}/attendees`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
};
export const useListEventAttendees = () => {
    const { token } = useAuth();
    return useCallback((appId, eventId) => _listEventAttendees(token, appId, eventId), [token]);
};

// =================== Map POIs ===================
const _listMapPois = (token, appId) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/map-pois`, { headers: { 'Authorization': `Bearer ${token}` } });
};
export const useListMapPois = () => {
    const { token } = useAuth();
    return useCallback((appId) => {
        if (!appId) return Promise.reject(new Error("App ID is required"));
        return _listMapPois(token, appId);
    }, [token]);
};

const _getMapPoi = (token, appId, poiId) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/map-pois/${poiId}`, { headers: { 'Authorization': `Bearer ${token}` } });
};
export const useGetMapPoi = () => {
    const { token } = useAuth();
    return useCallback((appId, poiId) => {
        if (!appId || !poiId) return Promise.reject(new Error("App ID and POI ID are required"));
        return _getMapPoi(token, appId, poiId);
    }, [token]);
};

const _createMapPoi = (token, appId, poiData) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/map-pois`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(poiData),
    });
};
export const useCreateMapPoi = () => {
    const { token } = useAuth();
    return useCallback((appId, poiData) => {
        if (!appId) return Promise.reject(new Error("App ID is required"));
        return _createMapPoi(token, appId, poiData);
    }, [token]);
};

const _updateMapPoi = (token, appId, poiId, poiData) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/map-pois/${poiId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(poiData),
    });
};
export const useUpdateMapPoi = () => {
    const { token } = useAuth();
    return useCallback((appId, poiId, poiData) => {
        if (!appId || !poiId) return Promise.reject(new Error("App ID and POI ID are required"));
        return _updateMapPoi(token, appId, poiId, poiData);
    }, [token]);
};

const _deleteMapPoi = (token, appId, poiId) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/map-pois/${poiId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
};
export const useDeleteMapPoi = () => {
    const { token } = useAuth();
    return useCallback((appId, poiId) => {
        if (!appId || !poiId) return Promise.reject(new Error("App ID and POI ID are required"));
        return _deleteMapPoi(token, appId, poiId);
    }, [token]);
};

// =================== Map Config ===================
const _getMapConfig = (token, appId) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/map-config`, { headers: { 'Authorization': `Bearer ${token}` } });
};
export const useGetMapConfig = () => {
    const { token } = useAuth();
    return useCallback((appId) => {
        if (!appId) return Promise.reject(new Error("App ID is required"));
        return _getMapConfig(token, appId);
    }, [token]);
};

const _updateMapConfig = (token, appId, configData) => {
    return fetchApi(`${API_BASE_URL}/apps/${appId}/map-config`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(configData)
    });
};
export const useUpdateMapConfig = () => {
    const { token } = useAuth();
    return useCallback((appId, configData) => {
        if (!appId) return Promise.reject(new Error("App ID is required"));
        return _updateMapConfig(token, appId, configData);
    }, [token]);
};

// =================== Brave Local Search ===================
const _braveLocalSearch = (token, appId, query) => {
    if (!appId || !query) return Promise.reject(new Error("App ID and query are required for Brave search"));
    const params = new URLSearchParams({ query });
    return fetchApi(`${API_BASE_URL}/apps/${appId}/map-pois/brave-local-search?${params.toString()}`, {
        method: 'GET', // GET request with query in URL
        headers: { 'Authorization': `Bearer ${token}` }
    });
};
export const useBraveLocalSearch = () => {
    const { token } = useAuth();
    // This hook returns the function to call, consistent with others
    return useCallback((appId, query) => {
        return _braveLocalSearch(token, appId, query);
    }, [token]);
};

// ================= App Users (Supabase Auth + Profiles) ===================
const _listAppUsers = (token, appId, page = 1, size = 10) => {
    if (!appId) return Promise.reject(new Error("App ID is required"));
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    return fetchApi(`${API_BASE_URL}/apps/${appId}/users?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
};
export const useListAppUsers = () => {
    const { token } = useAuth();
    return useCallback((appId, page, size) => {
        return _listAppUsers(token, appId, page, size);
    }, [token]);
};

const _getAppUserDetails = (token, appId, userId) => {
    if (!appId || !userId) return Promise.reject(new Error("App ID and User ID are required"));
    return fetchApi(`${API_BASE_URL}/apps/${appId}/users/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
};
export const useGetAppUserDetails = () => {
    const { token } = useAuth();
    return useCallback((appId, userId) => {
        return _getAppUserDetails(token, appId, userId);
    }, [token]);
};

const _getUserComments = (token, appId, userId) => {
    if (!appId || !userId) return Promise.reject(new Error("App ID and User ID are required"));
    return fetchApi(`${API_BASE_URL}/apps/${appId}/users/${userId}/comments`, { headers: { 'Authorization': `Bearer ${token}` } });
};
export const useGetUserComments = () => {
    const { token } = useAuth();
    return useCallback((appId, userId) => {
        return _getUserComments(token, appId, userId);
    }, [token]);
};

const _assignUserToOrganization = (token, appId, userId, organizationId) => {
    if (!appId || !userId || !organizationId) return Promise.reject(new Error("App ID, User ID, and Organization ID are required"));
    return fetchApi(`${API_BASE_URL}/apps/${appId}/users/${userId}/organizations/${organizationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
        // Body might be needed if assigning specific role later
    });
};
export const useAssignUserToOrganization = () => {
    const { token } = useAuth();
    return useCallback((appId, userId, organizationId) => {
        return _assignUserToOrganization(token, appId, userId, organizationId);
    }, [token]);
};

const _removeUserFromOrganization = (token, appId, userId, organizationId) => {
    if (!appId || !userId || !organizationId) return Promise.reject(new Error("App ID, User ID, and Organization ID are required"));
    return fetchApi(`${API_BASE_URL}/apps/${appId}/users/${userId}/organizations/${organizationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
    });
};
export const useRemoveUserFromOrganization = () => {
    const { token } = useAuth();
    return useCallback((appId, userId, organizationId) => {
        return _removeUserFromOrganization(token, appId, userId, organizationId);
    }, [token]);
};

const _deleteAppUser = (token, appId, userId) => {
    if (!appId || !userId) return Promise.reject(new Error("App ID and User ID are required"));
    return fetchApi(`${API_BASE_URL}/apps/${appId}/users/${userId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
};
export const useDeleteAppUser = () => {
    const { token } = useAuth();
    return useCallback((appId, userId) => {
        return _deleteAppUser(token, appId, userId);
    }, [token]);
};

const _updateAppUserPassword = (token, appId, userId, password) => {
    if (!appId || !userId) return Promise.reject(new Error("App ID and User ID are required"));
    return fetchApi(`${API_BASE_URL}/apps/${appId}/users/${userId}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password })
    });
};
export const useUpdateAppUserPassword = () => {
    const { token } = useAuth();
    return useCallback((appId, userId, password) => {
        return _updateAppUserPassword(token, appId, userId, password);
    }, [token]);
};

// --- RAG Service Functions --- //
const _getRagStatus = async (token, appId) => {
    if (!appId) throw new Error("App ID is required");
    return await fetchApi(`${API_BASE_URL}/apps/${appId}/rag/status`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
    });
};
export const useGetRagStatus = () => {
    const { token } = useAuth();
    return useCallback((appId) => _getRagStatus(token, appId), [token]);
};

const _startIndexing = async (token, appId) => {
    if (!appId) throw new Error("App ID is required");
    return await fetchApi(`${API_BASE_URL}/apps/${appId}/rag/index`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
};
export const useStartIndexing = () => {
    const { token } = useAuth();
    return useCallback((appId) => _startIndexing(token, appId), [token]);
};

// New hook for continuing indexing
const _continueIndexing = async (token, appId) => {
    if (!appId) throw new Error("App ID is required");
    return await fetchApi(`${API_BASE_URL}/apps/${appId}/rag/index/continue`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
};
export const useContinueIndexing = () => {
    const { token } = useAuth();
    return useCallback((appId) => _continueIndexing(token, appId), [token]);
};

// New hook for stopping indexing
const _stopIndexing = async (token, appId) => {
    if (!appId) throw new Error("App ID is required");
    return await fetchApi(`${API_BASE_URL}/apps/${appId}/rag/stop`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
};
export const useStopIndexing = () => {
    const { token } = useAuth();
    return useCallback((appId) => _stopIndexing(token, appId), [token]);
};

// --- Deprecated Hook Examples (Remove or keep commented) ---
/*
// Example of the old pattern - KEEPING ONE FOR REFERENCE
// It's generally better to use the individual hooks above now.
export const useListApps_Old = () => {
    const { token } = useAuth();
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchApps = useCallback(async () => {
        if (!token) return; // Don't fetch if no token
        setLoading(true);
        setError(null);
        try {
            const data = await _listApps(token); // Use the private function
            setApps(data || []);
        } catch (err) {
            setError(err.message || 'Failed to fetch apps');
            console.error("Failed to fetch apps:", err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchApps();
    }, [fetchApps]); // fetchApps is stable due to useCallback

    return { apps, loading, error, refreshApps: fetchApps };
};
*/

// --- Knowledge Base Storage Functions ---
// (Assuming these might be added later or elsewhere, adding a section header)

// Fetch knowledge base file count stats
const _getKnowledgeBaseStats = async (token, appId) => {
    if (!appId) throw new Error("App ID is required");
    return await fetchApi(`${API_BASE_URL}/apps/${appId}/knowledge-base/stats`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
    });
};
export const useGetKnowledgeBaseStats = () => {
    const { token } = useAuth();
    // This hook returns the function to call, consistent with others
    return useCallback((appId) => {
        if (!token) return Promise.reject(new Error('Not authenticated'));
        if (!appId) return Promise.reject(new Error("App ID is required"));
        return _getKnowledgeBaseStats(token, appId);
    }, [token]);
};

// Helper function for geocoding using the axios instance
// No need to pass token explicitly, the interceptor handles it.
const _geocodeAddress = async (address) => {
    if (!address) {
        throw new Error("Address is required for geocoding.");
    }
    // Use URLSearchParams to properly encode the address
    const params = new URLSearchParams({ address });
    try {
        // Use the imported 'api' (axios instance) which has the interceptor
        const response = await api.get(`/geocode?${params.toString()}`);
        return response.data; // Axios returns data directly in response.data
    } catch (error) {
        // Axios wraps errors, potentially in error.response.data
        const errorMessage = error.response?.data?.message || error.message || 'Failed to geocode address.';
        console.error("Axios geocoding error:", error.response || error);
        throw new Error(errorMessage);
    }
};

// Hook for geocoding an address via the backend
export const useGeocodeAddress = () => {
    // No need to get token here, interceptor uses localStorage
    // const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const geocode = useCallback(async (address) => {
        setLoading(true);
        setError(null);
        try {
            // Call the helper without the token
            const result = await _geocodeAddress(address);
            setLoading(false);
            // Assuming the backend returns { lat: number, lon: number }
            // Validate the result structure
            if (typeof result?.lat === 'number' && typeof result?.lon === 'number') {
                return result;
            } else {
                 // Handle cases where the backend might return success but invalid data
                 console.error('Invalid coordinates format received from server:', result);
                 throw new Error("Invalid coordinates received from server.");
            }
        } catch (err) {
            console.error("Geocoding hook error:", err);
            setError(err.message); // Error message is already processed by _geocodeAddress
            setLoading(false);
            throw err; // Re-throw error to be caught in the component
        }
    }, []); // Removed token from dependency array

    return { geocode, loading, error };
};

// --- Chat Group Tags ---
export const useListChatGroupTags = () => {
    const { token } = useAuth();
    return useCallback(async (appId) => {
        if (!appId) throw new Error("App ID is required");
        const options = {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/chat-group-tags`, options);
    }, [token]);
};

export const useCreateChatGroupTag = () => {
    const { token } = useAuth();
    return useCallback(async (appId, tagData) => {
        if (!appId) throw new Error("App ID is required");
        const options = {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
             },
            body: JSON.stringify(tagData)
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/chat-group-tags`, options);
    }, [token]);
};

export const useUpdateChatGroupTag = () => {
    const { token } = useAuth();
    return useCallback(async (appId, tagId, tagData) => {
        if (!appId || !tagId) throw new Error("App ID and Tag ID are required");
         const options = {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
             },
            body: JSON.stringify(tagData)
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/chat-group-tags/${tagId}`, options);
    }, [token]);
};

export const useDeleteChatGroupTag = () => {
    const { token } = useAuth();
    return useCallback(async (appId, tagId) => {
        if (!appId || !tagId) throw new Error("App ID and Tag ID are required");
         const options = {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/chat-group-tags/${tagId}`, options);
    }, [token]);
};

// --- Event Categories ---
export const useListEventCategories = () => {
    const { token } = useAuth();
    return useCallback(async (appId) => {
        if (!appId) throw new Error("App ID is required");
         const options = {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/event-categories`, options);
    }, [token]);
};

export const useCreateEventCategory = () => {
    const { token } = useAuth();
    return useCallback(async (appId, categoryData) => {
        if (!appId) throw new Error("App ID is required");
         const options = {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
             },
            body: JSON.stringify(categoryData)
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/event-categories`, options);
    }, [token]);
};

export const useUpdateEventCategory = () => {
    const { token } = useAuth();
    return useCallback(async (appId, categoryId, categoryData) => {
        if (!appId || !categoryId) throw new Error("App ID and Category ID are required");
         const options = {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
             },
            body: JSON.stringify(categoryData)
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/event-categories/${categoryId}`, options);
    }, [token]);
};

export const useDeleteEventCategory = () => {
    const { token } = useAuth();
    return useCallback(async (appId, categoryId) => {
        if (!appId || !categoryId) throw new Error("App ID and Category ID are required");
         const options = {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        };
        return await fetchApi(`${API_BASE_URL}/apps/${appId}/event-categories/${categoryId}`, options);
    }, [token]);
};

// --- Chat (AI Interaction) --- //

// Note: useChat hook from ai/react handles the stream itself
// This section is for *other* chat-related actions like generation

// --- NEW HOOK ---
const _generateChatGroupPost = (token, appId, messages) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/chat/generate-chat-group-post`,
    {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ messages }),
    }
);
export const useGenerateChatGroupPost = () => {
    const { token } = useAuth();
    return useCallback((appId, messages) => _generateChatGroupPost(token, appId, messages), [token]);
};
// --- END NEW HOOK ---


// --- Article Generation (if needed outside useChat, e.g., specific button) ---
// Example: If you had a button separate from the main chat to generate an article
const _generateArticleFromChat = (token, appId, messages) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/chat/generate-article`,
    {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ messages }),
    }
);
export const useGenerateArticleFromChat = () => {
    const { token } = useAuth();
    return useCallback((appId, messages) => _generateArticleFromChat(token, appId, messages), [token]);
};

// --- NEW HOOK ---
const _generateEventFromChat = (token, appId, messages) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/chat/generate-event`,
    {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ messages }),
    }
);
export const useGenerateEventFromChat = () => {
    const { token } = useAuth();
    return useCallback((appId, messages) => _generateEventFromChat(token, appId, messages), [token]);
};
// --- END NEW HOOK ---

// === AI‑Pipeline Hooks ===
// -- SOURCES --
const _listPipelineSources = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/sources`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListPipelineSources = () => {
  const { token } = useAuth();
  return useCallback((appId) => _listPipelineSources(token, appId), [token]);
};

const _createPipelineSource = (token, appId, data) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/sources`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(data) });
export const useCreatePipelineSource = () => {
  const { token } = useAuth();
  return useCallback((appId, data) => _createPipelineSource(token, appId, data), [token]);
};

const _updatePipelineSource = (token, appId, sourceId, data) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/sources/${sourceId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(data) });
export const useUpdatePipelineSource = () => {
  const { token } = useAuth();
  return useCallback((appId, sourceId, data) => _updatePipelineSource(token, appId, sourceId, data), [token]);
};

const _deletePipelineSource = (token, appId, sourceId) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/sources/${sourceId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
export const useDeletePipelineSource = () => {
  const { token } = useAuth();
  return useCallback((appId, sourceId) => _deletePipelineSource(token, appId, sourceId), [token]);
};

// -- DRAFTS --
const _listPipelineDrafts = (token, appId, type) => {
  const url = `${API_BASE_URL}/apps/${appId}/ai-pipeline/drafts${type ? `?type=${type}` : ''}`;
  return fetchApi(url, { headers: { 'Authorization': `Bearer ${token}` } });
};
export const useListPipelineDrafts = () => {
  const { token } = useAuth();
  return useCallback((appId, type) => _listPipelineDrafts(token, appId, type), [token]);
};

const _approvePipelineDraft = (token, appId, draftId, body={}) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/drafts/${draftId}/approve`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(body) });
export const useApprovePipelineDraft = () => {
  const { token } = useAuth();
  return useCallback((appId, draftId, body) => _approvePipelineDraft(token, appId, draftId, body), [token]);
};

// -- RUNS --
const _listPipelineRuns = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/runs`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListPipelineRuns = () => {
  const { token } = useAuth();
  return useCallback((appId) => _listPipelineRuns(token, appId), [token]);
};

const _triggerPipelineRun = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/runs`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
export const useTriggerPipelineRun = () => {
  const { token } = useAuth();
  return useCallback((appId) => _triggerPipelineRun(token, appId), [token]);
};

// -- RAW DOCUMENTS --
const _listRawDocuments = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/raw-documents`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListRawDocuments = () => {
  const { token } = useAuth();
  return useCallback((appId) => _listRawDocuments(token, appId), [token]);
};

// Modified to accept and pass options (like responseType)
const _getRawDocumentContent = (token, appId, docId, options = {}) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/raw-documents/${docId}/content`, {
   headers: { 'Authorization': `Bearer ${token}` },
   responseType: options.responseType || 'text' // Use options.responseType or default
 });
export const useGetRawDocumentContent = () => {
  const { token } = useAuth();
  // Modified useCallback to accept and pass options
  return useCallback((appId, docId, options) => _getRawDocumentContent(token, appId, docId, options), [token]);
};

// === End AI‑Pipeline Hooks ===

// --- New hook to trigger single source scrape ---
const _scrapeSingleSource = (token, appId, sourceId) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/sources/${sourceId}/scrape`, { 
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` }
});
export const useScrapeSingleSource = () => {
  const { token } = useAuth();
  return useCallback((appId, sourceId) => _scrapeSingleSource(token, appId, sourceId), [token]);
};

// === START: Add the correct useDiscoverSources hook ===
export function useDiscoverSources() {
  const queryClient = useQueryClient();
  // Get token needed for the API call within the mutation function
  const { token } = useAuth(); 
  
  return useMutation(
    // Function that performs the mutation: accepts appId and body data
    async ({ appId, body }) => {
      console.log(`[AppService] Calling discoverSources for appId: ${appId} with body:`, body);
      if (!token) throw new Error("Authentication token is missing.");

      // Use fetchApi helper or apiClient directly
      const url = `${API_BASE_URL}/apps/${appId}/ai-pipeline/discover-sources`;
      const options = {
        method: 'POST',
        headers: { 
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(body)
      };
      // Use fetchApi or adjust if using apiClient (axios instance)
      // Using fetchApi for consistency with other hooks in this file
      return await fetchApi(url, options); 
      
      // If using apiClient (axios) it would be:
      // const { data } = await apiClient.post(`/apps/${appId}/ai-pipeline/discover-sources`, body);
      // return data; 
    },
    {
      // React Query options
      onSuccess: (data, variables) => {
        console.log('[AppService] discoverSources successful, invalidating sources query...');
        // Invalidate sources query to refresh the list in AiPipelinePage
        queryClient.invalidateQueries(['pipelineSources', variables.appId]); 
        // If needed, invalidate other queries like raw docs
        // queryClient.invalidateQueries(['pipelineRawDocs', variables.appId]);
      },
      onError: (error) => {
        console.error('[AppService] Error in useDiscoverSources mutation:', error);
        // Consider showing a user-facing notification here
      }
      // Add other useMutation options if needed (e.g., onMutate, onSettled)
    }
  );
}
// === END: Add the correct useDiscoverSources hook ===

// Ensure the old useCallback-based hook definition below is removed or commented out
/* 
// Discover sources with optional doc context 
const _discoverSources = (token, appId, body={}) => fetchApi(`${API_BASE_URL}/apps/${appId}/ai-pipeline/discover-sources`, {
  method:'POST',
  headers:{ 'Content-Type':'application/json','Authorization':`Bearer ${token}`},
  body: JSON.stringify(body) // Send the body object
});
export const useDiscoverSources = () => {
  const { token } = useAuth();
  // Update this to accept the body object
  return useCallback((appId, body) => _discoverSources(token, appId, body), [token]);
};
*/

// --- AI Pipeline Draft Generation & Management --- //

// List AI Drafts
async function _listAiDrafts(token, appId, type = null) {
    let url = `${API_BASE_URL}/apps/${appId}/ai-pipeline/drafts`;
    if (type) {
        const params = new URLSearchParams({ type });
        url += `?${params.toString()}`;
    }
    // Correct: Pass url first, then options
    return fetchApi(url, { 
        method: 'GET', 
        headers: { 'Authorization': `Bearer ${token}` } 
    }); 
}
export const useListAiDrafts = (appId, type) => {
    const { token } = useAuth();
    return useCallback(() => _listAiDrafts(token, appId, type), [token, appId, type]);
};

// Generate AI Drafts from Prompt
async function _generateAiDrafts(token, appId, prompt, useRAG, usePerplexity, useImageSearch) {
    const body = { prompt, useRAG, usePerplexity, useImageSearch };
    const url = `${API_BASE_URL}/apps/${appId}/ai-pipeline/generate-drafts`;
    return fetchApi(url, { 
        method: 'POST', 
        headers: { 
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(body) 
    }); 
}
export const useGenerateAiDrafts = () => {
    const { token } = useAuth();
    return useCallback(async (appId, prompt, useRAG, usePerplexity, useImageSearch) => {
        return _generateAiDrafts(token, appId, prompt, useRAG, usePerplexity, useImageSearch);
    }, [token]);
};

// Update an AI Draft
async function _updateAiDraft(token, appId, draftId, updateData) {
    const url = `${API_BASE_URL}/apps/${appId}/ai-pipeline/drafts/${draftId}`;
    // Correct: Pass url first, then options
    return fetchApi(url, { 
        method: 'PUT', 
        headers: { 
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(updateData) 
    }); 
}
export const useUpdateAiDraft = () => {
    const { token } = useAuth();
    return useCallback(async (appId, draftId, updateData) => {
        return _updateAiDraft(token, appId, draftId, updateData);
    }, [token]);
};

// Delete an AI Draft
async function _deleteAiDraft(token, appId, draftId) {
    const url = `${API_BASE_URL}/apps/${appId}/ai-pipeline/drafts/${draftId}`;
    // Correct: Pass url first, then options
    return fetchApi(url, { 
        method: 'DELETE', 
        headers: { 'Authorization': `Bearer ${token}` } 
    }); 
}
export const useDeleteAiDraft = () => {
    const { token } = useAuth();
    return useCallback(async (appId, draftId) => {
        return _deleteAiDraft(token, appId, draftId);
    }, [token]);
};

// Auto-Create Articles from Drafts
async function _autoCreateArticlesFromDrafts(token, appId) {
    const url = `${API_BASE_URL}/apps/${appId}/ai-pipeline/auto-create-articles`;
    // Correct: Pass url first, then options
    return fetchApi(url, { 
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}` } 
        // No body needed
    }); 
}
export const useAutoCreateArticlesFromDrafts = () => {
    const { token } = useAuth();
    return useCallback(async (appId) => {
        return _autoCreateArticlesFromDrafts(token, appId);
    }, [token]);
};

// Add Missing Images to Drafts
async function _addMissingDraftImages(token, appId) {
    const url = `${API_BASE_URL}/apps/${appId}/ai-pipeline/drafts/add-images`;
    return fetchApi(url, { 
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}` } 
    }); 
}
export const useAddMissingDraftImages = () => {
    const { token } = useAuth();
    return useCallback(async (appId) => {
        return _addMissingDraftImages(token, appId);
    }, [token]);
};

// --- Vouchers ---
const _listVouchers = (token, appId) => fetchApi(`${API_BASE_URL}/apps/${appId}/vouchers`, { headers: { 'Authorization': `Bearer ${token}` } });
export const useListVouchers = () => {
    const { token } = useAuth();
    return useCallback((appId) => _listVouchers(token, appId), [token]);
};

const _createVoucher = (token, appId, voucherData) => fetchApi(
    `${API_BASE_URL}/apps/${appId}/vouchers`,
    {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(voucherData),
    }
);
export const useCreateVoucher = () => {
    const { token } = useAuth();
    return useCallback((appId, voucherData) => _createVoucher(token, appId, voucherData), [token]);
};
// --- End Vouchers ---